import { createPkcePair } from '@bettercomp/ams-spa-auth';
import { reportError } from 'utils/reporting';
import { config } from 'common/config';
import { createAuthBroadcastChannel, AuthBroadcastMessageType } from 'components/IdleWarning/constants';
import { pkceVerifierStore } from 'service/pkceVerifierStore';

const scope = 'phone+openid+profile';

export const getLoginUrl = async (state = window.location.pathname) => {
  const params = [
    `client_id=${config?.cognitoClientId}`,
    'response_type=code',
    `state=${window.btoa(state)}`,
    `scope=${scope}`,
    `redirect_uri=${window.location.origin}/auth/callback`,
  ];

  try {
    const { verifier, challenge } = await createPkcePair();
    pkceVerifierStore.put(verifier);
    params.push(`code_challenge=${challenge}`, 'code_challenge_method=S256');
  } catch (err) {
    // WebCrypto needs a secure context and sessionStorage can be unavailable;
    // fall back to the plain code flow — fetchAuthToken sends the verifier
    // only when one was stored, so both shapes stay valid end to end.
    reportError(err);
  }

  return `${config.cognitoApi}/login?${params.join('&')}`;
};

const getLogoutUrl = () => {
  const params = [
    `client_id=${config?.cognitoClientId}`,
    `logout_uri=${window.location.origin}/logout`,
  ];

  return `${config.cognitoApi}/logout?${params.join('&')}`;
};

export function logout(broadcast = true) {
  if (broadcast) {
    try {
      // notify all tabs about logout event
      const channel = createAuthBroadcastChannel();
      channel.postMessage(AuthBroadcastMessageType.Logout);
      channel.close();
    } catch (err) {
      reportError(err);
    }
  }

  window.location.assign(getLogoutUrl());
}

export const fetchAuthToken = async (code: string) => {
  // Conditional PKCE: a code minted from a getLoginUrl redirect carries a
  // stored verifier; codes from pre-PKCE bookmarks of the hosted UI or from
  // IdP-initiated SSO have none, and Cognito rejects an unexpected verifier —
  // so the field is present exactly when the challenge was.
  const verifier = pkceVerifierStore.take();

  const body = {
    grant_type: 'authorization_code',
    client_id: config?.cognitoClientId ?? '',
    code,
    scope,
    redirect_uri: `${window.location.origin}/auth/callback`,
    ...(verifier ? { code_verifier: verifier } : {}),
  };

  const response = await fetch(`${config.cognitoApi}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(body),
  });

  const result = JSON.parse(await response.text());

  if (!response.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const refreshAuthToken = async (refreshToken: string) => {
  const body = {
    grant_type: 'refresh_token',
    client_id: config?.cognitoClientId ?? '',
    refresh_token: refreshToken,
  };

  const response = await fetch(`${config.cognitoApi}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(body),
  });

  const result = JSON.parse(await response.text());

  if (!response.ok) {
    throw new Error(result.error);
  }

  return result;
};
